import { memo, useCallback, useMemo, useState } from 'react'
import type { AdCampaignPickStrategy, GapFillCampaign, GapFillOptions, LiveFillEntry } from '../../lib/api.js'
import { adBlockTotalDurationSec } from '../../lib/adBlockTiming.js'
import { formatAdSpotLabel } from '../../lib/adSpotLabel.js'
import { Spinner } from '../common/Spinner.js'
// La structure du composeur vit ici : toute boîte qui l'utilise hérite des
// deux colonnes, des rangées et du catalogue sans avoir à importer autre chose.
import './LiveFillComposer.css'

/**
 * Composition d'un comblage d'antenne, partagée par les trois surfaces qui la
 * demandent : fin manuelle d'un direct, décrochage d'un bloc de direct, et
 * préparation du décrochage préfait de la journée.
 *
 * Ce module ne connaît ni la géométrie de la grille ni les routes : il ne fait
 * que tenir une liste ordonnée d'éléments et proposer le catalogue. Le calage
 * appartient au serveur (`live-break-plan.ts`, `live-skip-plan.ts`) et la
 * résolution du contenu à `live-fill-catalog.service.ts` — c'est cette dernière
 * qui garantit que les trois surfaces acceptent exactement les mêmes contenus.
 */

export type FillKind = 'AD_BREAK' | 'AD_CAMPAIGN' | 'INFOSERVICE' | 'JINGLE' | 'INTERLUDE' | 'PROGRAM'

/** Un élément posé dans le comblage : ce qu'on affiche + ce qu'on envoie. */
export interface FillItem {
  uid:         string
  label:       string
  sublabel:    string
  durationSec: number
  kind:        FillKind
  /** Élément élastique : sa durée est celle qui reste, calculée à l'insertion. */
  fillRest?:   boolean
  entry:       LiveFillEntry
}

/** Libellé en clair d'un type de contenu, pour les sous-titres de liste. */
export const KIND_LABELS: Record<FillKind, string> = {
  AD_BREAK:    'Publicité',
  AD_CAMPAIGN: 'Campagne',
  INFOSERVICE: 'Infoservice',
  JINGLE:      'Élément d’antenne',
  INTERLUDE:   'Interlude',
  PROGRAM:     'Programme',
}

export const KIND_BADGES: Record<FillKind, string> = {
  AD_BREAK:    'PUB',
  AD_CAMPAIGN: 'CMP',
  INFOSERVICE: 'INFO',
  JINGLE:      'JGL',
  INTERLUDE:   'INT',
  PROGRAM:     'PRG',
}

/** Types de programmes d'habillage rangés dans l'onglet « Coming next ». */
export const COMING_NEXT_TYPES = ['COMING_NEXT', 'BANDE_ANNONCE']

export type CatalogTab = 'ADS' | 'INFOSERVICE' | 'JINGLES' | 'COMING_NEXT'

const CATALOG_TABS: Array<{ id: CatalogTab; label: string }> = [
  { id: 'ADS',         label: 'Publicités' },
  { id: 'INFOSERVICE', label: 'Infoservice' },
  { id: 'JINGLES',     label: 'Éléments d’antenne' },
  { id: 'COMING_NEXT', label: 'Coming next' },
]

/**
 * Durée d'un raccourci, en langage d'opérateur : « 30 s », « 2 min ».
 * `fmtSec` sert aux durées mesurées, pas aux valeurs rondes proposées.
 */
export function fmtPreset(sec: number) {
  return sec % 60 === 0 ? `${sec / 60} min` : `${sec} s`
}

export function fmtSec(sec: number) {
  const s = Math.max(0, Math.floor(sec))
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}m${String(ss).padStart(2, '0')}s`
  return `${m}m${String(ss).padStart(2, '0')}s`
}

/** Libellé opérateur d'un tirage de campagne, au singulier comme au pluriel. */
export function campaignPickLabel(strategy: AdCampaignPickStrategy, spotCount: number): string {
  const spots = spotCount > 1 ? `${spotCount} spots` : '1 spot'
  return strategy === 'RANDOM'
    ? `${spots} au hasard`
    : `${spots}, ${spotCount > 1 ? 'les moins diffusés' : 'le moins diffusé'}`
}

/**
 * Place à réserver pour un tirage : le pire cas, c'est-à-dire les spots les plus
 * longs de la campagne. Le serveur ne dépassera jamais cette réserve — il
 * enjambe un spot qui n'y tient pas — donc la fenêtre composée ici ne peut pas
 * être mise en défaut par le tirage du soir.
 */
export function campaignBudgetSec(
  campaign: GapFillCampaign,
  spotCount: number,
  transitionDurationMs?: number,
): number {
  const longest = [...campaign.spots]
    .sort((left, right) => right.durationSec - left.durationSec)
    .slice(0, spotCount)
    .map(spot => spot.durationSec)
  // Le contrat API et les durées de grille sont exprimés en secondes entières.
  // Une transition peut toutefois durer 500 ms : arrondir vers le haut garde
  // toute la réserve et évite à la fois le rejet Zod et un sous-dimensionnement.
  return Math.ceil(adBlockTotalDurationSec(longest, transitionDurationMs))
}

let uidSeq = 0
export const nextFillUid = () => `fill_${++uidSeq}`

// ─── État de la sélection ─────────────────────────────────────────────────────

/**
 * Liste ordonnée d'éléments et actions du catalogue.
 *
 * `fixedSec` compte les spots consécutifs comme un seul bloc pub, transition
 * configurée comprise : c'est la même règle que le serveur applique en posant le
 * bloc, sinon la durée affichée à l'opérateur dérive de ce qui est réellement
 * diffusé.
 */
export function useLiveFillSelection(options: GapFillOptions | null, initial: FillItem[] = []) {
  const [selected, setSelected] = useState<FillItem[]>(initial)

  const addItem = useCallback((item: Omit<FillItem, 'uid'>) => {
    setSelected(prev => [...prev, { ...item, uid: nextFillUid() }])
  }, [])

  const removeItem = useCallback((uid: string) => {
    setSelected(prev => prev.filter(item => item.uid !== uid))
  }, [])

  const moveItem = useCallback((uid: string, dir: -1 | 1) => {
    setSelected(prev => {
      const idx = prev.findIndex(item => item.uid === uid)
      const target = idx + dir
      if (idx < 0 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const [moved] = next.splice(idx, 1)
      next.splice(target, 0, moved)
      return next
    })
  }, [])

  const infoservice = options?.infoservice ?? null

  const addSpot = useCallback((
    spot: { id: string; title: string; durationSec: number },
    campaignName: string,
  ) => {
    addItem({
      label:       formatAdSpotLabel({ ...spot, campaignName }),
      sublabel:    'Publicité',
      durationSec: spot.durationSec,
      kind:        'AD_BREAK',
      entry:       { itemType: 'AD_BREAK', spotId: spot.id, durationSec: spot.durationSec },
    })
  }, [addItem])

  /**
   * Campagne : rien n'est nommé maintenant, le serveur tirera les spots au
   * moment où le décrochage sera posé. C'est ce qui fait tourner la campagne
   * d'un décrochage au suivant sans rouvrir le modèle.
   */
  const addCampaign = useCallback((
    campaign: GapFillCampaign,
    strategy: AdCampaignPickStrategy,
    spotCount: number,
  ) => {
    const durationSec = campaignBudgetSec(campaign, spotCount, options?.transitionDurationMs)
    addItem({
      label:    campaign.name,
      sublabel: `Campagne · ${campaignPickLabel(strategy, spotCount)}`,
      durationSec,
      kind:     'AD_CAMPAIGN',
      entry: {
        itemType: 'AD_CAMPAIGN', campaignId: campaign.id, pick: strategy, spotCount, durationSec,
      },
    })
  }, [addItem, options?.transitionDurationMs])

  const addProgram = useCallback((program: {
    id: string; title: string; durationSec: number; mediaAssetId: string | null; type: string
  }) => {
    addItem({
      label:       program.title,
      sublabel:    'Élément d’antenne',
      durationSec: program.durationSec,
      kind:        'JINGLE',
      entry: {
        itemType: 'JINGLE', programId: program.id, durationSec: program.durationSec,
        ...(program.mediaAssetId ? { mediaAssetId: program.mediaAssetId } : {}),
      },
    })
  }, [addItem])

  const addClip = useCallback((clip: {
    id: string; name: string; durationSec: number | null; clipType: string
  }) => {
    const durationSec = clip.durationSec ?? 30
    addItem({
      label: clip.name, sublabel: clip.clipType, durationSec, kind: 'INTERLUDE',
      entry: { itemType: 'INTERLUDE', clipId: clip.id, durationSec },
    })
  }, [addItem])

  const addInfoservice = useCallback((durationSec: number) => {
    if (!infoservice) return
    addItem({
      label: infoservice.title, sublabel: 'Infoservice', durationSec, kind: 'INFOSERVICE',
      entry: { itemType: 'INFOSERVICE', durationSec },
    })
  }, [addItem, infoservice])

  /** Infoservice élastique : il tient l'antenne jusqu'à la fin de la fenêtre. */
  const addInfoserviceRest = useCallback(() => {
    if (!infoservice) return
    addItem({
      label: infoservice.title, sublabel: 'Infoservice — comble le reste', durationSec: 0,
      kind: 'INFOSERVICE', fillRest: true,
      entry: { itemType: 'INFOSERVICE', fillRest: true },
    })
  }, [addItem, infoservice])

  const fixedSec = useMemo(() => {
    let total = 0
    let adRun: number[] = []
    const flush = () => {
      if (adRun.length) {
        total += adBlockTotalDurationSec(adRun, options?.transitionDurationMs)
        adRun = []
      }
    }
    for (const item of selected) {
      if (item.kind === 'AD_BREAK') adRun.push(item.durationSec)
      else if (!item.fillRest) { flush(); total += item.durationSec }
      else flush()
    }
    flush()
    return total
  }, [selected, options?.transitionDurationMs])

  const hasElastic = selected.some(item => item.fillRest)

  return {
    selected, setSelected, addItem, removeItem, moveItem,
    addSpot, addCampaign, addProgram, addClip, addInfoservice, addInfoserviceRest,
    fixedSec, hasElastic,
    entries: useMemo(() => selected.map(item => item.entry), [selected]),
  }
}

// ─── Liste des éléments retenus ───────────────────────────────────────────────

interface LiveFillSelectionProps {
  selected:   FillItem[]
  /** Durée attribuée à l'élément élastique, pour l'afficher à sa vraie taille. */
  elasticSec: number
  disabled?:  boolean
  emptyLabel?: string
  onMove:     (uid: string, dir: -1 | 1) => void
  onRemove:   (uid: string) => void
}

export const LiveFillSelection = memo(function LiveFillSelection({
  selected, elasticSec, disabled = false, emptyLabel, onMove, onRemove,
}: LiveFillSelectionProps) {
  if (selected.length === 0) {
    return (
      <div className="gap-fill__empty">
        {emptyLabel ?? 'Choisissez un contenu à droite pour l’ajouter ici'}
      </div>
    )
  }

  return (
    <div className="gap-fill__selected-list">
      {selected.map((item, idx) => (
        <div
          key={item.uid}
          className="gap-fill__selected-row"
          data-last={idx === selected.length - 1 || undefined}
        >
          <div className="gap-fill__selected-main">
            <span className={`gap-fill__kind is-${item.kind.toLowerCase()}`}>{KIND_BADGES[item.kind]}</span>
            <span className="live-skip__selected-text">
              <span className="gap-fill__selected-label">{item.label}</span>
              <span className="live-skip__selected-sub">{item.sublabel}</span>
            </span>
          </div>
          <div className="gap-fill__selected-meta">
            <span>{fmtSec(item.fillRest ? elasticSec : item.durationSec)}</span>
            <button
              type="button" className="tp-icon-button live-skip__reorder"
              onClick={() => onMove(item.uid, -1)} disabled={disabled || idx === 0}
              aria-label={`Monter ${item.label}`}
            >↑</button>
            <button
              type="button" className="tp-icon-button live-skip__reorder"
              onClick={() => onMove(item.uid, 1)} disabled={disabled || idx === selected.length - 1}
              aria-label={`Descendre ${item.label}`}
            >↓</button>
            <button
              type="button" className="tp-icon-button gap-fill__remove"
              onClick={() => onRemove(item.uid)} disabled={disabled}
              aria-label={`Retirer ${item.label}`}
            >×</button>
          </div>
        </div>
      ))}
    </div>
  )
})

// ─── Catalogue des contenus disponibles ───────────────────────────────────────

interface LiveFillCatalogProps {
  options:    GapFillOptions | null
  loading?:   boolean
  disabled?:  boolean
  hasElastic: boolean
  /** Place encore libre : désactive « combler tout le reste » quand il n'y en a plus. */
  remainingSec: number
  /** Masque l'infoservice élastique quand la fenêtre n'a pas de reste à combler. */
  allowElastic?: boolean
  onAddSpot:            (spot: { id: string; title: string; durationSec: number }, campaignName: string) => void
  /** Absent : le catalogue ne propose pas le tirage de campagne. */
  onAddCampaign?:       (campaign: GapFillCampaign, strategy: AdCampaignPickStrategy, spotCount: number) => void
  onAddInfoservice:     (durationSec: number) => void
  onAddInfoserviceRest: () => void
  onAddProgram:         (program: { id: string; title: string; durationSec: number; mediaAssetId: string | null; type: string }) => void
  onAddClip:            (clip: { id: string; name: string; durationSec: number | null; clipType: string }) => void
}

export const LiveFillCatalog = memo(function LiveFillCatalog({
  options, loading = false, disabled = false, hasElastic, remainingSec, allowElastic = true,
  onAddSpot, onAddCampaign, onAddInfoservice, onAddInfoserviceRest, onAddProgram, onAddClip,
}: LiveFillCatalogProps) {
  const [tab, setTab] = useState<CatalogTab>('ADS')
  // Nombre de spots à tirer, retenu par campagne : deux campagnes voisines n'ont
  // pas le même format de bloc.
  const [pickCounts, setPickCounts] = useState<Record<string, number>>({})

  const campaigns   = options?.campaigns ?? []
  const jingles     = options?.jingles ?? []
  const clips       = options?.clips ?? []
  const infoservice = options?.infoservice ?? null

  const comingNextPrograms = useMemo(() => jingles.filter(j => COMING_NEXT_TYPES.includes(j.type)), [jingles])
  const jinglePrograms     = useMemo(() => jingles.filter(j => !COMING_NEXT_TYPES.includes(j.type)), [jingles])
  const comingNextClips    = useMemo(() => clips.filter(c => c.clipType === 'COMING_NEXT'), [clips])
  const interludeClips     = useMemo(() => clips.filter(c => c.clipType !== 'COMING_NEXT'), [clips])

  return (
    <div className="gap-fill__catalog live-skip__catalog">
      <div className="live-skip__tabs" role="tablist" aria-label="Contenu disponible">
        {CATALOG_TABS.map(entry => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            className="live-skip__tab"
            data-active={tab === entry.id || undefined}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="live-skip__catalog-list">
        {loading && <div className="agenda-editor__loading"><Spinner /> Chargement…</div>}

        {!loading && tab === 'ADS' && (
          campaigns.length === 0 ? (
            <div className="agenda-editor__empty">Aucune campagne active</div>
          ) : campaigns.map(campaign => (
            <div key={campaign.id} className="live-skip__group">
              <div className="gap-fill__group-title">
                {campaign.name}
                {campaign.advertiserName ? ` · ${campaign.advertiserName}` : ''}
              </div>
              {campaign.spots.length === 0 ? (
                <div className="agenda-editor__empty agenda-editor__empty--compact">Aucun spot actif</div>
              ) : (
                <>
                  {campaign.spots.map(spot => (
                    <button
                      key={spot.id} type="button" className="gap-fill__available"
                      onClick={() => onAddSpot(spot, campaign.name)} disabled={disabled}
                    >
                      <div className="gap-fill__available-title">
                        {formatAdSpotLabel({ ...spot, campaignName: campaign.name })}
                      </div>
                      <div className="gap-fill__available-meta">PUB · {fmtSec(spot.durationSec)}</div>
                    </button>
                  ))}
                  {campaign.spots.length > 1 && (
                    <button
                      type="button" className="live-skip__add-all"
                      onClick={() => campaign.spots.forEach(spot => onAddSpot(spot, campaign.name))}
                      disabled={disabled}
                    >
                      + Tout le bloc ({campaign.spotCount} spots · {fmtSec(campaign.totalDurationSec)})
                    </button>
                  )}
                  {onAddCampaign && (
                    <CampaignPicker
                      campaign={campaign}
                      count={pickCounts[campaign.id] ?? 1}
                      disabled={disabled}
                      transitionDurationMs={options?.transitionDurationMs}
                      onCountChange={value => setPickCounts(prev => ({ ...prev, [campaign.id]: value }))}
                      onPick={onAddCampaign}
                    />
                  )}
                </>
              )}
            </div>
          ))
        )}

        {!loading && tab === 'INFOSERVICE' && (
          !infoservice ? (
            <div className="agenda-editor__empty">Aucun infoservice sélectionné pour cette chaîne</div>
          ) : (
            <>
              <div className="gap-fill__group-title">
                Infoservice sélectionné{infoservice.mediaDate ? ` · ${infoservice.mediaDate}` : ''}
              </div>
              <button
                type="button" className="gap-fill__available"
                onClick={() => onAddInfoservice(infoservice.durationSec)} disabled={disabled}
              >
                <div className="gap-fill__available-title">{infoservice.title}</div>
                <div className="gap-fill__available-meta">INFOSERVICE · {fmtSec(infoservice.durationSec)}</div>
              </button>
              {allowElastic && (
                <button
                  type="button" className="gap-fill__available"
                  onClick={onAddInfoserviceRest} disabled={disabled || hasElastic || remainingSec <= 0}
                >
                  <div className="gap-fill__available-title">Combler tout le reste</div>
                  <div className="gap-fill__available-meta">
                    {hasElastic
                      ? 'Déjà dans le remplissage'
                      : remainingSec > 0
                        ? `L'infoservice tient l'antenne jusqu'au bout · ${fmtSec(remainingSec)}`
                        : 'La fenêtre est déjà comblée'}
                  </div>
                </button>
              )}
            </>
          )
        )}

        {!loading && tab === 'JINGLES' && (
          jinglePrograms.length === 0 && interludeClips.length === 0 ? (
            <div className="agenda-editor__empty">Aucun élément d’antenne disponible</div>
          ) : (
            <>
              {jinglePrograms.length > 0 && <div className="gap-fill__group-title">Éléments d’antenne</div>}
              {jinglePrograms.map(program => (
                <button
                  key={program.id} type="button" className="gap-fill__available"
                  onClick={() => onAddProgram(program)} disabled={disabled}
                >
                  <div className="gap-fill__available-title">{program.title}</div>
                  <div className="gap-fill__available-meta">{program.type} · {fmtSec(program.durationSec)}</div>
                </button>
              ))}
              {interludeClips.length > 0 && <div className="gap-fill__group-title">Interludes</div>}
              {interludeClips.map(clip => (
                <button
                  key={clip.id} type="button" className="gap-fill__available"
                  onClick={() => onAddClip(clip)} disabled={disabled}
                >
                  <div className="gap-fill__available-title">{clip.name}</div>
                  <div className="gap-fill__available-meta">
                    {clip.clipType} · {clip.durationSec ? fmtSec(clip.durationSec) : '?'}
                  </div>
                </button>
              ))}
            </>
          )
        )}

        {!loading && tab === 'COMING_NEXT' && (
          comingNextPrograms.length === 0 && comingNextClips.length === 0 ? (
            <div className="agenda-editor__empty">Aucun coming next disponible</div>
          ) : (
            <>
              {comingNextPrograms.map(program => (
                <button
                  key={program.id} type="button" className="gap-fill__available"
                  onClick={() => onAddProgram(program)} disabled={disabled}
                >
                  <div className="gap-fill__available-title">{program.title}</div>
                  <div className="gap-fill__available-meta">{program.type} · {fmtSec(program.durationSec)}</div>
                </button>
              ))}
              {comingNextClips.map(clip => (
                <button
                  key={clip.id} type="button" className="gap-fill__available"
                  onClick={() => onAddClip(clip)} disabled={disabled}
                >
                  <div className="gap-fill__available-title">{clip.name}</div>
                  <div className="gap-fill__available-meta">
                    COMING NEXT · {clip.durationSec ? fmtSec(clip.durationSec) : '?'}
                  </div>
                </button>
              ))}
            </>
          )
        )}
      </div>
    </div>
  )
})

// ─── Tirage d'une campagne ────────────────────────────────────────────────────

interface CampaignPickerProps {
  campaign: GapFillCampaign
  count:    number
  disabled: boolean
  transitionDurationMs?: number
  onCountChange: (count: number) => void
  onPick:        (campaign: GapFillCampaign, strategy: AdCampaignPickStrategy, count: number) => void
}

/**
 * « Une campagne, pas des spots ». L'opérateur réserve de la place à une
 * campagne et choisit seulement la règle de tirage ; les spots, eux, sont
 * désignés par le serveur au moment où le décrochage part à l'antenne. Sur un
 * décrochage préfait, c'est ce qui fait tourner la campagne d'un décrochage au
 * suivant sans rien rouvrir.
 *
 * La durée annoncée est le **pire cas** (les spots les plus longs de la
 * campagne) : le tirage ne peut donc jamais faire déborder la fenêtre composée
 * ici.
 */
const CampaignPicker = memo(function CampaignPicker({
  campaign, count, disabled, transitionDurationMs, onCountChange, onPick,
}: CampaignPickerProps) {
  const maxCount = Math.max(1, campaign.spots.length)
  const safeCount = Math.min(Math.max(1, count), maxCount)
  const budgetSec = campaignBudgetSec(campaign, safeCount, transitionDurationMs)
  const countId = `campaign-pick-${campaign.id}`

  return (
    <div className="live-skip__campaign">
      <div className="live-skip__campaign-head">
        <span className="live-skip__campaign-title">Tirage à chaque décrochage</span>
        <span className="live-skip__campaign-budget">réserve ≤ {fmtSec(budgetSec)}</span>
      </div>
      <div className="live-skip__campaign-controls">
        <label className="live-skip__campaign-count" htmlFor={countId}>
          <span>Spots</span>
          <input
            id={countId}
            type="number"
            min={1}
            max={maxCount}
            step={1}
            value={safeCount}
            disabled={disabled || maxCount === 1}
            onChange={event => onCountChange(
              Math.min(maxCount, Math.max(1, Number(event.target.value) || 1)),
            )}
          />
        </label>
        <button
          type="button"
          className="tp-button tp-button--ghost live-skip__campaign-button"
          disabled={disabled}
          onClick={() => onPick(campaign, 'LEAST_AIRED', safeCount)}
          title="Le serveur prend les spots les moins diffusés de la campagne, au moment de poser le décrochage"
        >
          + Le moins diffusé
        </button>
        <button
          type="button"
          className="tp-button tp-button--ghost live-skip__campaign-button"
          disabled={disabled}
          onClick={() => onPick(campaign, 'RANDOM', safeCount)}
          title="Le serveur tire au hasard dans la campagne, au moment de poser le décrochage"
        >
          + Au hasard
        </button>
      </div>
    </div>
  )
})
